import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { MessageSquareQuote, Plus } from "lucide-react";
import { toast } from "sonner";
import { TestimonialCard } from "./TestimonialCard";
import { NpsAvailablePicker } from "./NpsAvailablePicker";
import { EmptyState, SkeletonGrid } from "./common";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useConfirm } from "../../components/useConfirm";

/* ======================================================================== */
/* TAB 1 — TESTIMONIOS                                                       */
/* ======================================================================== */
export function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [minScore, setMinScore] = useState(9);
  const [editingId, setEditingId] = useState(null);

  // Iter24.d: confirm dialog reutilizable
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/landing/testimonials");
      setItems(data.items || data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudieron cargar testimonios");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAvailable = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/landing/testimonials/available?min_score=${minScore}`);
      setAvailable(data.items || data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudieron cargar respuestas NPS disponibles");
    }
  }, [minScore]);

  useEffect(() => { load(); }, [load]);

  const openPicker = async () => {
    await loadAvailable();
    setPickerOpen(true);
  };

  const addFromNps = async (nps) => {
    try {
      const payload = {
        nps_response_id: nps.nps_response_id || nps.id,
        display_name: (nps.user_email || "Usuario").split("@")[0],
        display_role: "Cliente Procure",
        display_entity: "",
        display_quote: nps.comment || "",
        is_visible: false, // ✋ por defecto NO visible, admin activa después de editar
        sort_order: items.length,
      };
      await api.post("/admin/landing/testimonials", payload);
      toast.success("Testimonio agregado. Edítalo y actívalo para que aparezca en el landing.");
      setPickerOpen(false);
      await load();
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) {
        toast.error("Este testimonio ya estaba agregado.");
      } else {
        toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo agregar testimonio");
      }
    }
  };

  const removeTestimonial = async (id) => {
    const ok = await confirm({
      title: "Eliminar testimonio",
      description: "¿Eliminar este testimonio del landing? El NPS original NO se elimina, solo se quita de la lista pública.",
      confirmLabel: "Sí, eliminar",
      destructive: true,
      testIdPrefix: "testimonial-delete-confirm",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/landing/testimonials/${id}`);
      toast.success("Testimonio eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const toggleVisible = async (item) => {
    try {
      await api.patch(`/admin/landing/testimonials/${item.id}`, { is_visible: !item.is_visible });
      toast.success(item.is_visible ? "Testimonio ocultado" : "Testimonio visible en el landing");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo actualizar");
    }
  };

  return (
    <div className="space-y-4" data-testid="testimonials-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="font-serif text-lg text-gray-900">Testimonios curados</h2>
          <p className="text-sm text-gray-600">
            Selecciona respuestas NPS de promotores (score ≥ 9) y edita su presentación pública.
          </p>
        </div>
        <Button
          onClick={openPicker}
          className="rounded-sm bg-primary hover:bg-primary/90"
          data-testid="btn-add-testimonial"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Seleccionar desde NPS
        </Button>
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : items.length === 0 ? (
        <EmptyState
          icon={MessageSquareQuote}
          title="Aún no hay testimonios curados"
          subtitle="Haz clic en 'Seleccionar desde NPS' para elegir respuestas de promotores y mostrarlas en el landing."
        />
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4" data-testid="testimonials-list">
          {items.map((t) => (
            <li key={t.id} data-testid={`testimonial-${t.id}`}>
              <TestimonialCard
                item={t}
                editing={editingId === t.id}
                onEdit={() => setEditingId(t.id)}
                onCloseEdit={() => setEditingId(null)}
                onSaved={load}
                onRemove={() => removeTestimonial(t.id)}
                onToggleVisible={() => toggleVisible(t)}
              />
            </li>
          ))}
        </ul>
      )}

      {pickerOpen && (
        <NpsAvailablePicker
          items={available}
          minScore={minScore}
          onMinScoreChange={(v) => { setMinScore(v); }}
          onReload={loadAvailable}
          onPick={addFromNps}
          onClose={() => setPickerOpen(false)}
        />
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
